import React, { useContext, useEffect, useMemo, useState } from "react";
import { ApiContext } from "../../../context/MyContext";
import { Toaster } from "../../../common/Toaster/Toaster";
import { Drawer } from "../../../common/comms/Drawer";
import { EmptyState } from "../../../common/comms/EmptyState";
import { BookingStatusModal } from "./BookingStatusModal";
import { BookingsTab } from "./BookingsTab";
import { BookingsTabs } from "./BookingsTabs";
import { ClientActivityPanel } from "./ClientActivityPanel";
import { MyShiftsTab } from "./MyShiftsTab";
import { OffShiftBookingModal } from "./OffShiftBookingModal";
import { WorkspaceCalendar } from "./WorkspaceCalendar";
import { resolveStatusAuthority } from "./bookingAuthority";
import { findFocusedBooking, isShowingFocusMonth } from "./bookingFocus";
import { statusTitleFor } from "./bookingStatus";
import { clientTrailForEmployee } from "./clientActivity";
import { departmentIdByTitle } from "./coachRoster";
import {
  buildWeek,
  callTypeLabelsById,
  formatShortDate,
  formatTimeOfDay,
  minutesOfDay,
} from "./slotGrid";
import { formatLocalDate } from "../../../utils/formatLocalDate";
import { useBookingStatuses } from "./useBookingStatuses";
import { useBookings } from "./useBookings";
import { useCoachShifts, useShiftCallTypes } from "./useCoachShifts";

const MONTH_LABEL = (year, month) =>
  new Date(year, month, 1).toLocaleDateString("en-GB", {
    month: "long",
    year: "numeric",
  });

/**
 * The whole of somebody's call calendar on one screen: their **Bookings** (a
 * day agenda under a month/day calendar) and their **Shifts** (a week table of
 * when they work, which the server turns into bookable slots).
 *
 * **Every data hook is called here, once, and handed down.** `BookingsTab` and
 * `MyShiftsTab` are controlled panels that do not fetch: the calendar above
 * the agenda needs the month to draw its dots, and View slots needs the
 * bookings of whichever week the shifts table is on to mark slots as taken.
 *
 * The two panels keep separate dates, deliberately. The agenda is about a
 * **day**; shifts repeat every week, so their picker chooses a **week**, and
 * that week's bookings are read through `useBookings` for each month it
 * touches, only while the Shifts tab is up — the same cache entries the agenda
 * uses, so it costs nothing when the months coincide.
 *
 * Serves all three callers — a coach's own screen, a manager's own screen, and
 * a manager or executive reading one coach — through `employeeId` (whose
 * calendar) and `shiftsReadOnly`.
 *
 * @param {Object} props
 * @param {string} [props.employeeId] - Whose calendar. Defaults to the caller's.
 * @param {string} [props.coachName] - Named when viewing someone else.
 * @param {string} [props.departmentTitle] - That coach's department, which
 *   decides their call types. Omitted for the caller's own calendar, whose
 *   department comes from their roles.
 * @param {string[]} [props.departmentChoices] - Departments the Shifts slot
 *   preview may be read against, when no role decides it: an Executive coach's
 *   department comes from each member's subscription, so their own calendar
 *   offers a switch. Ignored when `departmentTitle` is given.
 * @param {boolean} [props.shiftsReadOnly=true] - Hide every way of changing shifts.
 * @param {{ shifts?: string }} [props.tabLabels] - Overrides the Shifts sub-tab
 *   label: "My shifts" on your own calendar, "Shifts" on somebody else's.
 * @param {string} [props.idPrefix='bk-tab'] - Prefix for the sub-tab ids, so
 *   two workspaces never collide on one page.
 * @param {import('./bookingFocus').BookingFocus|null} [props.focus] - A call to
 *   open on arrival (a chat card's "View booking"): jump to its month and day,
 *   then open the activity drawer on it.
 * @param {() => void} [props.onFocusHandled] - Called once `focus` has been
 *   opened, or could not be found on this calendar.
 */
export const BookingsWorkspace = ({
  employeeId: targetEmployeeId,
  coachName,
  departmentTitle,
  departmentChoices,
  shiftsReadOnly = true,
  tabLabels,
  idPrefix = "bk-tab",
  focus = null,
  onFocusHandled,
}) => {
  const choosableDepartments = !departmentTitle && departmentChoices?.length ? departmentChoices : null;
  const [pickedDepartment, setPickedDepartment] = useState(choosableDepartments?.[0]);
  const today = useMemo(() => new Date(), []);
  const [view, setView] = useState({
    year: today.getFullYear(),
    month: today.getMonth(),
  });
  const [selectedDate, setSelectedDate] = useState(today);
  // Any day in the week the shifts table is labelled with.
  const [weekDate, setWeekDate] = useState(today);
  const [subTab, setSubTab] = useState("bookings");
  const [monthView, setMonthView] = useState(false);
  const [activeClient, setActiveClient] = useState(null);
  const [statusTarget, setStatusTarget] = useState(null);
  const [offShiftOpen, setOffShiftOpen] = useState(false);
  const [toast, setToast] = useState(null);

  const viewingCoach = Boolean(targetEmployeeId);

  const { employeeData, departmentsWithRoles } = useContext(ApiContext);
  const bookings = useBookings({
    ...view,
    employeeId: targetEmployeeId,
    coachScoped: viewingCoach,
  });
  const { statusIndex, weekdayIdByNumber, coachShiftTypeId } = useBookingStatuses();
  const shifts = useCoachShifts({
    employeeId: targetEmployeeId,
    departmentTitle: departmentTitle || (choosableDepartments ? pickedDepartment : undefined),
    weekdayIdByNumber,
    coachShiftTypeId,
  });
  // Names each booking's call type for the agenda's chip. The same cached list
  // `useCoachShifts` reads, so this costs no request of its own.
  const shiftCallTypes = useShiftCallTypes();
  const callTypeLabels = useMemo(
    () => callTypeLabelsById(shiftCallTypes.rows),
    [shiftCallTypes.rows]
  );

  const showingShifts = subTab === "shifts";

  // The shifts table's week can straddle two months, and neither need be the
  // agenda's. One `useBookings` per end of the week, switched on only while
  // the Shifts tab is showing (View slots is the only reader); when a month
  // matches another hook's, useCachedList serves the same entry.
  const week = useMemo(() => buildWeek(weekDate), [weekDate]);
  const weekStart = week[0].date;
  const weekEnd = week[6].date;
  const weekBookingsA = useBookings({
    year: weekStart.getFullYear(),
    month: weekStart.getMonth(),
    employeeId: targetEmployeeId,
    coachScoped: viewingCoach,
    enabled: showingShifts,
  });
  const weekBookingsB = useBookings({
    year: weekEnd.getFullYear(),
    month: weekEnd.getMonth(),
    employeeId: targetEmployeeId,
    coachScoped: viewingCoach,
    enabled: showingShifts,
  });
  const weekBookingsByDate = useMemo(
    () => ({ ...weekBookingsA.myBookingsByDate, ...weekBookingsB.myBookingsByDate }),
    [weekBookingsA.myBookingsByDate, weekBookingsB.myBookingsByDate]
  );

  // Whose calendar this is decides what may be written on it. Resolved once
  // here rather than per row: every booking on the panel sits on the same
  // coach's slots, because `myBookingsByDate` is already narrowed to them.
  const statusAuthority = useMemo(
    () =>
      resolveStatusAuthority({
        employeeData,
        departmentsWithRoles,
        slotEmployeeId: bookings.employeeId,
        viewingCoach,
      }),
    [employeeData, departmentsWithRoles, bookings.employeeId, viewingCoach]
  );

  // A booking to open on arrival, in two steps because the rows arrive a month
  // at a time. First show the month and day the call is on...
  const focusId = focus?.bookingId;
  useEffect(() => {
    if (!focus) return;
    setSubTab("bookings");
    if (focus.start) {
      setView({ year: focus.start.getFullYear(), month: focus.start.getMonth() });
      setSelectedDate(focus.start);
    }
    // Keyed on the id: `focus.start` is a Date rebuilt from the same URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusId]);

  // ...then, once that month's rows are in, open the drawer on the call. A
  // booking that is not among them is on a calendar this caller is not looking
  // at (or cannot see): say so rather than leave the click unanswered.
  useEffect(() => {
    if (!focus || !bookings.hasMonthData || !isShowingFocusMonth(focus, view)) return;
    const record = findFocusedBooking(bookings.bookings, focus);
    if (record) {
      const start = new Date(record.start_time);
      setActiveClient({
        id: record.user_id,
        name: record.user_name || "Unknown User",
        dayKey: Number.isNaN(start.getTime()) ? undefined : formatLocalDate(start),
        focusBookingId: record.id,
      });
    } else {
      setToast({
        message: "That booking isn't on this calendar.",
        type: "error",
        id: Date.now(),
      });
    }
    onFocusHandled?.();
    // `bookings.bookings` changes identity when the month's rows arrive, which
    // is the moment this is waiting for.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusId, bookings.hasMonthData, bookings.bookings, view.year, view.month]);

  /**
   * Open the status editor for one call.
   *
   * Closes the activity drawer first, wherever the action came from. `Drawer`
   * decides an outside click with a bare `contains()` test on its own panel, so
   * leaving it open behind the modal would make every click inside the modal
   * read as an outside click there — the same reason `ClientActivityPanel`
   * closes itself before opening the client profile.
   */
  const openStatusEditor = (booking) => {
    setActiveClient(null);
    setStatusTarget(booking);
  };

  const submitStatus = async (targetToken, reason) => {
    // Resolves the moment the server accepts the write, so the modal closes on
    // one round trip. `setStatus` re-reads the agenda's month in the
    // background.
    await bookings.setStatus({
      booking: statusTarget,
      targetToken,
      statusIndex,
      reason,
    });
    // A cancellation frees time View slots would otherwise still show as
    // booked. The week hooks share cache entries with the agenda's month, so
    // these are usually the same request, deduped. **Not awaited**: nothing on
    // screen waits for them, and the Shifts panel is a tab away.
    weekBookingsA.refresh();
    weekBookingsB.refresh();
    setToast({
      message: `Marked ${statusTarget?.user_name || "this call"} as ${statusTitleFor(
        statusIndex,
        targetToken
      )}.`,
      type: "success",
      id: Date.now(),
    });
  };

  // Same reason as `openStatusEditor`: never a Modal over the open Drawer.
  const openOffShift = () => {
    setActiveClient(null);
    setOffShiftOpen(true);
  };

  const handleOffShiftBooked = ({ startsAt, userName }) => {
    setOffShiftOpen(false);
    // Straight to the booked day — not `handleMonthChange`, which clears it.
    setView({ year: startsAt.getFullYear(), month: startsAt.getMonth() });
    setSelectedDate(startsAt);
    bookings.refresh();
    weekBookingsA.refresh();
    weekBookingsB.refresh();
    setToast({
      message: `Booked ${userName} for ${formatShortDate(startsAt)}, ${formatTimeOfDay(
        minutesOfDay(startsAt)
      )}.`,
      type: "success",
      id: Date.now(),
    });
  };

  const handleMonthChange = (year, month) => {
    setView({ year, month });
    // The selection belonged to the month that just left. Both tabs cleared it
    // on a month change before this, and for the same reason: a day number
    // carried into a new month is a different day.
    setSelectedDate(null);
    setActiveClient(null);
  };

  // The month's calls, narrowed to the client whose row was clicked — every
  // call, not one per client as the agenda shows, each with its history. The
  // panel narrows it again to the clicked day (`activeClient.dayKey`) until
  // asked to show the whole month.
  const activeTrail = useMemo(
    () =>
      activeClient
        ? clientTrailForEmployee(bookings.bookings, {
            clientId: activeClient.id,
            employeeId: bookings.employeeId,
          })
        : [],
    [activeClient, bookings.bookings, bookings.employeeId]
  );

  // Both prerequisites are the same for either panel, and neither is worth a
  // request without. Checked once here rather than once per panel.
  //
  // Only reachable on the caller's own screen: a coach view names its employee
  // id by definition, and the roster won't open a coach who has none.
  if (!bookings.hasEmployeeProfile) {
    return (
      <EmptyState
        title="You don't have a coach profile"
        hint="Bookings and shifts are recorded against an employee record. Ask an admin to verify your account, then reload."
      />
    );
  }

  if (!bookings.hasEventType) {
    return (
      <EmptyState
        title="Bookings isn't configured yet"
        hint="No booking event type is registered for FlexBoard, so bookings and shifts can't be read. This needs a backend change."
      />
    );
  }

  return (
    <div className="space-y-5">
      <BookingsTabs
        value={subTab}
        onChange={setSubTab}
        items={[
          { id: "bookings", label: "Bookings" },
          { id: "shifts", label: tabLabels?.shifts || "Shifts" },
        ]}
        // Never "Bookings views": on a manager's screen this strip sits
        // inside a tab of that name's own tablist, and two tablists with one
        // accessible name is one a screen reader cannot tell apart.
        label={coachName ? `${coachName} views` : "Calendar views"}
        idPrefix={idPrefix}
      />

      <div
        role="tabpanel"
        id={`${idPrefix}-${subTab}-panel`}
        aria-labelledby={`${idPrefix}-${subTab}`}
        className="space-y-5"
      >
        {showingShifts ? (
          <MyShiftsTab
            shifts={shifts}
            bookingsByDate={weekBookingsByDate}
            weekDate={weekDate}
            onWeekChange={setWeekDate}
            readOnly={shiftsReadOnly}
            title={tabLabels?.shifts || "Shifts"}
            coachName={coachName}
            departmentChoices={choosableDepartments}
            onDepartmentChange={setPickedDepartment}
          />
        ) : (
          <>
          <section className="rounded-xl border border-gray-200 bg-white p-4">
            <WorkspaceCalendar
              year={view.year}
              month={view.month}
              onMonthChange={handleMonthChange}
              selectedDate={selectedDate}
              onSelectDate={setSelectedDate}
              markedByDate={bookings.myBookingsByDate}
              markLabel="has bookings"
              busy={bookings.busy}
              monthView={monthView}
              onMonthViewChange={setMonthView}
            />
          </section>
          <BookingsTab
            selectedDate={selectedDate}
            bookingsByDate={bookings.myBookingsByDate}
            hasMonthData={bookings.hasMonthData}
            error={bookings.error}
            refresh={bookings.refresh}
            viewingCoach={viewingCoach}
            onOpenClient={setActiveClient}
            onSetStatus={openStatusEditor}
            statusAuthority={statusAuthority}
            statusIndex={statusIndex}
            callTypeLabels={callTypeLabels}
            onBookOffShift={openOffShift}
          />
          </>
        )}
      </div>

      <Drawer
        isOpen={Boolean(activeClient)}
        onClose={() => setActiveClient(null)}
        title="Client activity"
        width={420}
        // The panel pins its own header and scrolls only the activity list.
        bodyClassName="flex-1 min-h-0 overflow-hidden"
      >
        {activeClient ? (
          <ClientActivityPanel
            // Remounted per client and day, so "Show all" never carries over —
            // and per focused call, so a rating chip always opens its own.
            key={`${activeClient.id}-${activeClient.dayKey ?? ""}-${activeClient.focusBookingId ?? ""}`}
            clientId={activeClient.id}
            clientName={activeClient.name}
            dayKey={activeClient.dayKey}
            focusBookingId={activeClient.focusBookingId}
            initialShowAll={activeClient.showAll}
            trail={activeTrail}
            monthLabel={MONTH_LABEL(view.year, view.month)}
            onClose={() => setActiveClient(null)}
            // The same setter the agenda opens a client with, handed over so
            // the profile's Back can put this drawer back exactly as it was —
            // the same day, and the month view if that was showing.
            onReopen={setActiveClient}
            onSetStatus={openStatusEditor}
            statusAuthority={statusAuthority}
            statusIndex={statusIndex}
          />
        ) : null}
      </Drawer>

      <BookingStatusModal
        booking={statusTarget}
        authority={statusAuthority}
        statusIndex={statusIndex}
        saving={bookings.saving}
        onSubmit={submitStatus}
        onClose={() => setStatusTarget(null)}
      />

      {offShiftOpen ? (
        <OffShiftBookingModal
          onClose={() => setOffShiftOpen(false)}
          coachId={bookings.employeeId}
          coachName={coachName}
          ownCalendar={!viewingCoach}
          departmentTitle={departmentTitle}
          departmentId={
            departmentTitle ? departmentIdByTitle(departmentsWithRoles, departmentTitle) : null
          }
          departmentChoices={["Fitness", "Nutritionist"]
            .map((title) => ({ title, id: departmentIdByTitle(departmentsWithRoles, title) }))
            .filter((choice) => choice.id)}
          coachShiftTypeId={coachShiftTypeId}
          shifts={shifts}
          callTypeLabels={callTypeLabels}
          initialDate={selectedDate}
          onBooked={handleOffShiftBooked}
        />
      ) : null}

      {/*
        Safe beside any other Toaster on the page: each instance assigns itself
        a containerId, which keeps react-toastify's registry from merging them.
      */}
      {toast?.message ? (
        <Toaster key={toast.id} message={toast.message} type={toast.type} />
      ) : null}
    </div>
  );
};
